import { Component, Injector, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import { ClaimsBaseComponent } from "../../claim-base.component";
import { UiOptionsModel } from '../../../ui/ui-options.model';
import { TransactionInfoService } from 'src/app/services/transaction-info.service';
import {takeUntil} from "rxjs/operators";
import {UtilitiesService} from "../../../utilities/utilities.service";
import {UiDropdownSelectComponent} from "../../../ui/ui-dropdown-select/ui-dropdown-select.component";
import {TransactionInfo} from "../../../model/transaction-info.model";

@Component({
    selector: 'app-duitnow',
    templateUrl: './duitnow.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DuitNowComponent extends ClaimsBaseComponent implements OnInit {

    payeeOptions: UiOptionsModel[] = [];
    payeeIdTypeOptions: UiOptionsModel[] = [];
    payeeList: any[] = [];
    duitNowForm: FormGroup;
    isCorporate: boolean = false;
    transactionInfo: TransactionInfo;
    isPolicyHolderIdUsed: boolean = false;
    showPayeeTextField: boolean = false;

    @ViewChild('DUITNOW_PAYEE_ID', {static: false}) payeeIdDropdown: UiDropdownSelectComponent;

    constructor(private fb: FormBuilder,
        private injector: Injector) {
        super(injector);
        this.transactionInfo = this.injector.get(TransactionInfoService).getTransactionInfo();
        this.isCorporate = this.claim.getPolicyHolderType() === 'C';
        this.isPolicyHolderIdUsed = this.isNotNullOrUndefinedStr(UtilitiesService.stripNonAlphaNumeric(this.claim.getPolicyHolderId())) &&
            (UtilitiesService.stripNonAlphaNumeric(this.claim.getPolicyHolderId())?.toLowerCase() ===
                UtilitiesService.stripNonAlphaNumeric(this.transactionInfo.getAuthIdNumber())?.toLowerCase() ||
                this.transactionInfo.getAuthIdNumber()?.toLowerCase() === 'summys');
        this.showPayeeTextField = this.isCorporate || this.transactionInfo.isGenericClaim();
    }

    ngOnInit() {
        this.populatePayeeOptions();
        this.populatePayeeIdTypeOptions();

        // pre-populate payee details with the selected insured and id with NRIC or BRN
        if (!this.isNotNullOrUndefinedStr(this.claim.getProxyName()) && !this.isNotNullOrUndefinedStr(this.claim.getProxyId())) {
            const getSelectedPayee = (name: string, id: string) =>
                this.payeeList?.find(payee => payee.name?.toLowerCase() === name?.toLowerCase() &&
                    UtilitiesService.stripNonAlphaNumeric(payee.id?.toLowerCase()) === UtilitiesService.stripNonAlphaNumeric(id?.toLowerCase()));

            let selectedPayee = getSelectedPayee(this.claim.getClaimantName(), this.claim.getClaimantIdNumber());
            let defaultProxyIdType = 'NRIC';

            if (this.transactionInfo.isGenericClaim()) {
                selectedPayee = getSelectedPayee(this.claim.getPolicyHolderName(), this.claim.getPolicyHolderId());
                if (this.isCorporate) {
                    defaultProxyIdType = 'BRN';
                }
            }

            if (selectedPayee) {
                this.claim.setProxyName(selectedPayee.name);
                this.claim.setProxyId(selectedPayee.id);
                this.claim.setManuallyAddedInsured(selectedPayee.isManuallyAdded);
                this.claim.setProxyIdType(defaultProxyIdType);
                this.claim.setProxyIdTypeDesc(defaultProxyIdType);
            }
        }

        this.duitNowForm = this.fb.group(
            {
                payee: [this.claim.getProxyName()],
                payeeIdType: [this.claim.getProxyIdType()],
                payeeId: [this.claim.getProxyId()],
                countryPassport: [this.claim.getProxyCountryCode()]
            });
    }

    ngAfterViewInit() {
        this.duitNowForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                this.claim.setProxyName(this.duitNowForm.get('payee').value);
                this.claim.setProxyIdType(this.duitNowForm.get('payeeIdType').value);
                this.claim.setProxyIdTypeDesc(super.getSelectedRadioText('payeeIdType'));
                this.claim.setProxyId(this.duitNowForm.get('payeeId').value);
                this.claim.setProxyCountryCode(this.duitNowForm.get('countryPassport').value);
                this.claim.setProxyCountryDesc(super.getTextInComponent('countryPassport'));
            });

        this.duitNowForm.get('payee').valueChanges.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(data => {
                this.payeeChange(data);
            });

        this.duitNowForm.get('payeeIdType').valueChanges.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(data => {
                this.validateCountryPassport(data);
                if (this.isCorporate && this.isPolicyHolderIdUsed && this.transactionInfo.isTravelProduct()) {
                    this.populatePayeeDetails(data);
                }
            });
    }

    populatePayeeDetails(data: string) {
        const payeeFormControl = this.duitNowForm.get('payee') as FormControl;
        const payeeIdFormControl = this.duitNowForm.get('payeeId') as FormControl;
        // find the relevant payee based on the selected id type
        const payee = this.payeeList.find(payee => data?.toLowerCase() == 'brn'? payee.isPolicyHolder : !payee.isPolicyHolder);
        if (payee) {
            payeeFormControl.setValue(payee.name, { emitEvent: false });
            payeeIdFormControl.setValue(payee.id, { emitEvent: false });
            this.claim.setManuallyAddedInsured(payee.isManuallyAdded);
        }
    }

    populatePayeeOptions() {
        // Corporate policyholder
        if (this.isCorporate) {
            if (this.isPolicyHolderIdUsed) {
                // When the policyholder is Corporate and if the authentication was done using BRN then both BRN and NRIC/PASSPORT/ARMYID should appear
                // policyholder should be on payee list
                this.addPolicyHolderOnPayeeList();
                // manually added insured person should be on payee list for travel
                if (this.transactionInfo.isTravelProduct()) {
                    this.addInsuredPersonsOnPayeeList(this.claim.getClaimantName(), this.claim.getClaimantIdNumber());
                }
            } else {
                // When the policyholder is Corporate and if the authentication was done using NRIC/PASSPORT or BRN doesn't exist
                // then only NRIC/PASSPORT/ARMYID should appear
                // only the matched or manually added insured person should be on payee list
                let matchedInsuredPerson = this.transactionInfo.getMatchedInsuredPerson();
                if (matchedInsuredPerson) {
                    this.addInsuredPersonsOnPayeeList(matchedInsuredPerson.getInsuredName(), matchedInsuredPerson.getInsuredPersonNationalIdNumber());
                } else {
                    this.addInsuredPersonsOnPayeeList(this.claim.getClaimantName(), this.claim.getClaimantIdNumber());
                }
            }


        } else {
            // Personal policyholder
            this.addPolicyHolderOnPayeeList();
            if (this.transactionInfo.isTravelProduct()) {
                this.addInsuredPersonsOnPayeeList();
            }
        }

        // add payee on the dropdown
        this.payeeList.forEach(payee => this.payeeOptions.push(new UiOptionsModel(payee.name, payee.name)));
    }

    payeeChange(payeeName: string, idTypeChange?: boolean) {
        const payeeIdTypeFormControl = this.duitNowForm.get('payeeIdType') as FormControl;
        const payeeIdFormControl = this.duitNowForm.get('payeeId') as FormControl;

        let self = this;
        self.payeeList.forEach(function (payee) {
           if (payee.name == payeeName) {
               payeeIdFormControl.setValue(payee.id, { emitEvent: false });

               if (payee.isPolicyHolder && self.isCorporate && self.isPolicyHolderIdUsed) {
                   payeeIdTypeFormControl.setValue('BRN');
               } else {
                   payeeIdTypeFormControl.setValue('NRIC');
               }

               if (idTypeChange == true) {
                   const payeeFormControl = self.duitNowForm.get('payee') as FormControl;
                   payeeFormControl.setValue(payee.name, { emitEvent: false });
               }

               self.claim.setManuallyAddedInsured(payee.isManuallyAdded);
           }
        });
    }

    validateCountryPassport(data: string) {
        if (data?.toLowerCase() == 'passport') {
            this.duitNowForm.get('countryPassport').enable({onlySelf: false, emitEvent: false});
        } else {
            this.duitNowForm.get('countryPassport').reset();
            this.duitNowForm.get('countryPassport').disable({onlySelf: false, emitEvent: false});
        }
    }

    addPolicyHolderOnPayeeList() {
        if (this.claim.getPolicyHolderName() && UtilitiesService.stripNonAlphaNumeric(this.claim.getPolicyHolderId())) {
            this.payeeList.push({ id: this.claim.getPolicyHolderId(), name: this.claim.getPolicyHolderName(),
                isManuallyAdded : false, isPolicyHolder: true });
        }
    }

    addInsuredPersonsOnPayeeList(claimantName?: string, claimantIdNumber?: string) {
        let insuredPersons = this.injector.get(TransactionInfoService).getTransactionInfo().getInsuredPersonsList();

        if (insuredPersons && insuredPersons.length > 0) {
            const policyHolderName = this.claim.getPolicyHolderName()?.toLowerCase();
            const policyHolderId = this.claim.getPolicyHolderId()?.toLowerCase();

            insuredPersons.forEach(insured => {
                const insuredId = UtilitiesService.stripNonAlphaNumeric(insured.getInsuredPersonNationalIdNumber())?.toLowerCase();
                const insuredName = insured.getInsuredName()?.toLowerCase();

                if (insuredId) {
                    const isMatch = claimantName && claimantIdNumber
                        ? insuredName === claimantName?.toLowerCase() && insuredId === claimantIdNumber?.toLowerCase()
                        : insuredName !== policyHolderName && insuredId !== policyHolderId;

                    if (isMatch) {
                        this.payeeList.push({
                            name: insured.getInsuredName(),
                            id: UtilitiesService.stripNonAlphaNumeric(insured.getInsuredPersonNationalIdNumber()),
                            isManuallyAdded: !this.isNotNullOrUndefined(insured.getRiskNumber())
                        });
                    }
                }
            });
        }
    }

    populatePayeeIdTypeOptions() {
        if (this.isCorporate && this.isPolicyHolderIdUsed) {
            this.payeeIdTypeOptions.push(new UiOptionsModel('BRN', 'BRN'));
        }

        if (this.transactionInfo.isTravelProduct() || (this.transactionInfo.isGenericClaim() && !this.isCorporate && this.isPolicyHolderIdUsed)) {
            this.payeeIdTypeOptions.push(new UiOptionsModel('NRIC', 'NRIC'));
            this.payeeIdTypeOptions.push(new UiOptionsModel('Passport', 'Passport No.'));
            this.payeeIdTypeOptions.push(new UiOptionsModel('Army ID', 'Army ID'));
        }
    }

}
